import { CSSProperties } from 'react';
import Logo from 'assets/images/hjlogo.png';

import {
  SPLASH_ACCENT,
  SPLASH_BACKDROP,
  SPLASH_FONT,
  SPLASH_LOGO_WIDTH
} from 'components/SplashScreen/splashTheme';

export const OFFLINE_TITLE = 'No internet connection';
/**
 * Launch-specific, and deliberately NOT ConnectionWarning's NO_INTERNET_MESSAGE.
 *
 * That one reads "You have lost your internet connection", which is a
 * statement about a session that was working a moment ago. At a cold launch
 * the player never had a connection to lose, and telling them they lost
 * something they never had sends them hunting for a problem that is not
 * there. The actionable half is kept deliberately close to it, so the two
 * states still read as the same diagnosis.
 *
 * This wording is the one reviewed on device and signed off, and the ticket
 * was updated to match it. Do not "unify" it with ConnectionWarning's string
 * without re-reading the paragraph above.
 */
export const OFFLINE_MESSAGE =
  'We could not reach the tables. Check your Wi-Fi or mobile data, then try again.';
export const OFFLINE_RETRY_LABEL = 'Try again';
export const OFFLINE_RETRY_BUSY_LABEL = 'Checking...';

type OfflineLaunchScreenProps = {
  onRetry: () => void;
  /** True while a retry is in flight, so the button cannot be pressed twice. */
  busy?: boolean;
};

/**
 * Shown instead of the app when a cold launch finds no network at all.
 *
 * The player is held here rather than dropped into the lobby, because the
 * lobby cannot open its socket without a network: it would sit empty behind a
 * connection toast with nothing to act on. This screen says what is wrong and
 * gives them the one action that can fix it.
 *
 * Retry is a button, not a background backoff loop. A device with no network
 * has nothing to poll for, and the player usually knows exactly what they need
 * to do (turn Wi-Fi back on, leave the lift) long before a timer would notice.
 *
 * Deliberately styled off the same tokens as the branded splash it replaces,
 * so the handover reads as one screen changing its mind rather than two
 * different screens.
 */
const OfflineLaunchScreen = ({ onRetry, busy = false }: OfflineLaunchScreenProps) => (
  <div style={containerStyle}>
    <div style={contentStyle}>
      <img src={Logo} alt='Hijack Poker' style={logoStyle} />
      {/* One live region over the title and copy together: a retry that finds
          nothing changes neither, so there is nothing to re-announce, and the
          button carries its own state. */}
      <div role='status' aria-live='polite' style={copyStyle}>
        <h1 style={titleStyle}>{OFFLINE_TITLE}</h1>
        <p style={messageStyle}>{OFFLINE_MESSAGE}</p>
      </div>
      <button type='button' onClick={onRetry} disabled={busy} style={buttonStyle(busy)}>
        {busy ? OFFLINE_RETRY_BUSY_LABEL : OFFLINE_RETRY_LABEL}
      </button>
    </div>
  </div>
);

export default OfflineLaunchScreen;

const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: SPLASH_BACKDROP,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Landscape phones can leave less height than this content needs. Scroll it
  // rather than centring it out of view, and keep the copy clear of the
  // notch/home-indicator insets on both orientations.
  overflowY: 'auto',
  padding:
    'max(1.5rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) max(1.5rem, env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))',
  boxSizing: 'border-box',
  // Above the splash: the two overlap for a tick during the handover (see
  // SplashScreenContext's deferred hide), and this is the one that should win.
  zIndex: 1001
};

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1.25rem',
  maxWidth: '26rem',
  margin: 'auto'
};

const logoStyle: CSSProperties = {
  width: SPLASH_LOGO_WIDTH
};

const copyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
  textAlign: 'center'
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.35rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  color: 'rgba(255, 255, 255, 0.92)',
  fontFamily: SPLASH_FONT,
  textWrap: 'balance'
};

const messageStyle: CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  lineHeight: 1.5,
  color: 'rgba(255, 255, 255, 0.7)',
  fontFamily: SPLASH_FONT
};

const buttonStyle = (busy: boolean): CSSProperties => ({
  marginTop: '0.5rem',
  // 44px tall and wide enough to hit with a thumb, per the platform minimum.
  minHeight: '44px',
  padding: '0 2rem',
  border: 'none',
  borderRadius: '999px',
  background: SPLASH_ACCENT,
  color: '#1c1a14',
  fontSize: '1rem',
  fontWeight: 800,
  fontFamily: SPLASH_FONT,
  letterSpacing: '0.03em',
  cursor: busy ? 'default' : 'pointer',
  opacity: busy ? 0.6 : 1,
  boxShadow: '0 0 18px rgba(233, 140, 38, 0.35)'
});
